import React, { useState, useMemo, useCallback, useRef } from "react";
import styles from "./Hand2CorrectionComponent.module.css";
import MtiButton from "../../../Buttons/MtiButton";
import CommonDropDown, {
  CommonDropDownDisplayValue,
} from "../../../Common/DropDown/CommonDropDown";
import CommonTable from "../../../Common/CommonTable";
import {
  Hand2ThetaCorrectionResult,
  Hand2ThetaCorrectionPointResult,
} from "../../../../Model/Hand2ThetaCorrectionResult";
import { Column } from "react-table";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { ESuiteMode, EPsrState } from "../../../../Model/ModuleState";
import { useEffect } from "react";
import CalibrationApi from "../../../../api/CalibrationApi";
import useCalibrationLog from "../../../Hooks/useCalibrationLog";
import LayoutWithLog from "../LayoutWithLog";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../../../redux/actions/NotificationAction";
import { ENotificationType } from "../../../../Model/ENotificationType";
import { ECommonProcessState } from "../../../../Model/ECommonProcessState";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import usePersistState from "../../../../Utility/usePersistState";
import {
  calibrationSignalRMap,
  calibrationTabMap,
} from "../SettingsStorageRi1000";
import { HubConnection, HubConnectionBuilder } from "@aspnet/signalr";
import config from "../../../../config";

interface Props {
  setOnApply: (onApply: { execute: () => void; disabled: boolean }) => void;
  setOnCancel: (onCancel: { execute: () => void; disabled: boolean }) => void;
  setIsCalibrationUpdated: (isUpdated: boolean) => void;
  setResetHandler: (onReset: {
    execute: () => void;
    disabled: boolean;
  }) => void;
  setRunningTabKey: React.Dispatch<React.SetStateAction<string>>;
}

export default function Hand2CorrectionComponent({
  setOnApply,
  setOnCancel,
  setIsCalibrationUpdated,
  setResetHandler,
  setRunningTabKey,
}: Props) {
  const dispatch = useDispatch();
  const [processState, setProcessState] = usePersistState(
    ECommonProcessState.Initial,
    "HAND_2_CORRECTION_PROCESS_STATE"
  );
  const suiteState = useTypedSelector(
    (state) => state.moduleStateStore.moduleStateById["Suite"]?.suite
  );
  const suiteMode = suiteState?.suiteMode ?? ESuiteMode.Error;
  const psrModules = useTypedSelector((state) =>
    getModules(state, EModuleType.PSR)
  );
  const psrIdentifier = psrModules?.[0]?.identifier;
  const psrState = useTypedSelector(
    (state) => getModuleState(state, psrIdentifier).psr
  );

  // GET LOG LOGIC
  const [logs] = useCalibrationLog(calibrationSignalRMap.hand2Correction);

  useEffect(() => {
    if (suiteMode == ESuiteMode.AutoCalibrating) {
      setProcessState(ECommonProcessState.Processing);
      setAllowRunCalibration(false);
      return;
    }

    if (suiteMode == ESuiteMode.Normal) {
      if (processState === ECommonProcessState.Processing) {
        if (psrState?.state === EPsrState.Fail) {
          setProcessState(ECommonProcessState.Fault);
          setRunningTabKey("");
        } else if (psrState?.state === EPsrState.Available) {
          setProcessState(ECommonProcessState.Finished);
          setRunningTabKey("");
        }
      }
      setAllowRunCalibration(true);
      return;
    }

    if (suiteMode == ESuiteMode.ArmCrashed || suiteMode == ESuiteMode.Error) {
      setProcessState(ECommonProcessState.Initial);
      return;
    }
  }, [suiteMode, suiteState]);

  // Target Selection
  const [selectedTarget, setSelectedTarget] = useState("");
  const [targetList, setTargetList] = useState(
    [] as CommonDropDownDisplayValue<string>[]
  );

  const onSelectedTargetChange = (newTarget: string) => {
    setSelectedTarget(newTarget);
  };

  useEffect(() => {
    if (correctionResultData.length > 0) {
      const dataIndex = correctionResultData.findIndex(
        (data) => data.targetName == selectedTarget
      );
      if (dataIndex != -1) {
        setCorrectionResultPointData(
          correctionResultData[dataIndex].resultPoints
        );
      }
    }
  }, [selectedTarget]);

  const [correctionResultData, setCorrectionResultData] = useState(
    [] as Hand2ThetaCorrectionResult[]
  );

  // Correction Result Table
  const [correctionResultPointData, setCorrectionResultPointData] = useState(
    [] as Hand2ThetaCorrectionPointResult[]
  );
  const resultColumn: Column<Hand2ThetaCorrectionPointResult>[] = useMemo(
    () =>
      [
        {
          id: "location",
          Header: "Location",
          accessor: (row: Hand2ThetaCorrectionPointResult) => row.location,
          Cell: (cell: any) => (
            <div style={{ textAlign: "center" }}>{cell.value}</div>
          ),
        },
        {
          id: "hand-1-theta",
          Header: "Hand 1 Theta (deg)",
          accessor: (row: Hand2ThetaCorrectionPointResult) =>
            roundToTwoDecimal(row.hand1Theta),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
        {
          id: "hand-2-theta",
          Header: "Hand 2 Theta (deg)",
          accessor: (row: Hand2ThetaCorrectionPointResult) =>
            roundToTwoDecimal(row.hand2Theta),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
        {
          id: "hand-2-correction",
          Header: "Hand 2 Correction (deg)",
          accessor: (row: Hand2ThetaCorrectionPointResult) =>
            roundToTwoDecimal(row.hand2Correction),
          Cell: (cell: any) => (
            <div style={{ textAlign: "right" }}>{cell.value}</div>
          ),
        },
      ] as Column<Hand2ThetaCorrectionPointResult>[],
    []
  );

  const [allowRunCalibration, setAllowRunCalibration] = useState(true);

  const runCalibration = () => {
    setRunningTabKey(calibrationSignalRMap.hand2Correction);
    CalibrationApi.runHand2ThetaCorrection().then(() => {
      setProcessState(ECommonProcessState.Started);
    });
  };

  const stopCalibration = () => {
    CalibrationApi.stopHand2ThetaCorrection();
  };

  const onApply = () => {
    CalibrationApi.saveHand2ThetaCorrection()
      .then(() => {
        dispatch(
          sendNotification(
            ENotificationType.Success,
            "The changes have been saved successfully"
          )
        );
        setCalibrationState(false);
      })
      .catch(() => {
        dispatch(
          sendNotification(
            ENotificationType.Error,
            "Saving changes encountered an error"
          )
        );
      });
  };

  const onCancel = () => {
    CalibrationApi.revertHand2ThetaCorrection().then(() => {
      setCalibrationState(false);
      CalibrationApi.getHand2ThetaCorrectionResult().then((result) => {
        setCorrectionResultData(result);
        setProcessState(ECommonProcessState.Initial);
      });
    });
  };

  useEffect(() => {
    setResetHandler({
      execute: () => {
        setCalibrationState(false);
        CalibrationApi.getHand2ThetaCorrectionResult().then((result) => {
          setCorrectionResultData(result);
          setProcessState(ECommonProcessState.Initial);
        });
      },
      disabled: false,
    });
  }, []);

  const setCalibrationState = (isUpdated: boolean) => {
    setOnApply({
      execute: onApply,
      disabled: !isUpdated,
    });
    setOnCancel({
      execute: onCancel,
      disabled: !isUpdated,
    });
    setIsCalibrationUpdated(isUpdated);
  };

  // reset result table, target selector, and selected target when resultData is changed
  useEffect(() => {
    if (correctionResultData.length > 0) {
      setSelectedTarget(correctionResultData[0].targetName);
      setTargetList(
        correctionResultData.map((corrResult) => {
          return {
            disabled: false,
            display: corrResult.targetName,
            value: corrResult.targetName,
          };
        })
      );
      setCorrectionResultPointData(correctionResultData[0].resultPoints);
    } else {
      setCorrectionResultPointData([]);
      setTargetList([]);
    }
  }, [correctionResultData]);

  useEffect(() => {
    switch (processState) {
      case ECommonProcessState.Finished:
        CalibrationApi.getHand2ThetaCorrectionResult().then((result) => {
          setCorrectionResultData(result);
          setProcessState(ECommonProcessState.Initial);
          setCalibrationState(true);
        });
        break;
      default:
        break;
    }
  }, [processState]);

  useEffect(() => {
    if (targetList.length > 0) {
      selectedTarget === ""
        ? setSelectedTarget(targetList[0].value)
        : setSelectedTarget(selectedTarget);
    } else {
      setSelectedTarget("");
    }
  }, [targetList]);

  useEffect(() => {
    CalibrationApi.getHand2ThetaCorrectionResult().then((result) => {
      setCorrectionResultData(result);
    });
  }, []);

  const targetListIsEmpty = targetList.length < 1;

  return (
    <div className={styles["outer-border"]}>
      <LayoutWithLog logMessages={logs ?? []}>
        <LayoutWithLog.Content>
          <div className={styles["calibration-view"]}>
            <div className={styles["calibration-control"]}>
              <div className={styles["label-child"]}>
                Calibrating Hand 2 Theta
              </div>
              <div className={styles["button-group"]}>
                <MtiButton
                  variant="secondary"
                  colorVariant="black"
                  onClick={stopCalibration}
                  disabled={allowRunCalibration}
                >
                  STOP
                </MtiButton>
                <MtiButton
                  variant="primary"
                  colorVariant="black"
                  onClick={runCalibration}
                  disabled={!allowRunCalibration}
                >
                  RUN
                </MtiButton>
              </div>
            </div>

            <div className={styles["result-selector"]}>
              <div className={styles["label"]}>Result</div>
              <div className={styles["target-selector"]}>
                <div className={styles["label"]}>Target Name</div>
                <div className={styles["dropdown"]}>
                  <CommonDropDown<string>
                    value={selectedTarget}
                    id="target-select"
                    onChange={onSelectedTargetChange}
                    dropDownDisplayValues={targetList}
                    style={{
                      height: "38px",
                      width: "100%",
                    }}
                    disabled={targetListIsEmpty}
                  />
                </div>
              </div>
            </div>

            <div className={styles["result-table-view"]}>
              <CommonTable<Hand2ThetaCorrectionPointResult>
                data={correctionResultPointData}
                columns={resultColumn}
                style={{
                  height: "fit-content",
                  padding: 0,
                }}
                variant="no-separator"
                enableFilter={false}
                enablePagination={true}
                enableSelection={false}
                enableMultiSelect={false}
                enableManualSorting={false}
                pageLineSize={10}
              />
            </div>
          </div>
        </LayoutWithLog.Content>
      </LayoutWithLog>
    </div>
  );
}

function roundToTwoDecimal(num: number) {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}
